import { ActionTree } from 'vuex';
import Api, { Error as ApiError } from '../../../api/index';
import ApiUrl from '../../../api/url';
import { IdPType, UserSetting, UserSettingState } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { checkFatalError } from '../../fatal-error-filter';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { userSetting } from './index';

/**
 * ユーザードメイン設定情報取得失敗時のエラーハンドリング用関数
 * @param {any} dispatch Vuex変数
 * @param {any} commit Vuex変数
 * @param resData APIレスポンスボディ
 * @returns {Promise<void>}
 */
const beforeLoginAnyFailureHandler = async ({ dispatch, commit }, resData: ApiError) => {
    try {
        await checkFatalError({ dispatch }, resData);
        // アプリケーションエラー時
        commit(types.BEFORE_LOGIN_FAILURE, resData.errors[0]);
    } catch (error) {
        // システムエラー・予期せぬエラー時
        commit(types.FATAL_FAILURE);
    }
};

export const actions: ActionTree<UserSettingState, RootState> = {
    /**
     * ユーザー設定情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, category: string) {
        try {
            commit(types.SHOW_START, category);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.UserSetting.SHOW, params: { category: category } },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, { userSetting: resData });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ユーザー設定情報更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {UserSetting} userSetting 更新するユーザー設定情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, { userSetting, category }) {
        try {
            commit(types.UPDATE_START, category);
            const resData = await dispatch(
                'auth/api',
                { method: 'put', url: ApiUrl.UserSetting.UPDATE, params: userSetting },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserSetting.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * ログイン前情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async beforeLogin({ dispatch, commit }) {
        try {
            commit(types.BEFORE_LOGIN_START);
            const resData = await Api.get(ApiUrl.UserSetting.BEFORE_LOGIN); // 未ログイン時に使用するため、ラップAPIは使用しない
            // 成功時
            commit(types.BEFORE_LOGIN_SUCCESS, { userSetting: resData });
        } catch (resData) {
            // 4XX系・5XX系・?XX系エラー時
            beforeLoginAnyFailureHandler({ dispatch, commit }, resData);
        }
    },

    /**
     * パラメーターのAD・ドメイン情報で接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} ad 接続先AD情報
     * @param {any} domain 接続先ドメイン情報
     * @returns {Promise<void>}
     */
    async connection({ dispatch, commit, rootState }, { ad, domain }) {
        try {
            commit(types.CONNECTION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.UserSetting.CONNECTION,
                    params: { AD: ad, domain: domain },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CONNECTION_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserSetting.CONNECTED);
                dispatch('toastMessage', `${domain.domain_name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CONNECTION_FAILURE, params);
        }
    },

    /**
     * ドメインユーザー取込処理（同期処理）
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async import({ dispatch, commit, rootState }) {
        try {
            commit(types.IMPORT_START);
            const resData = await dispatch('auth/api', { method: 'post', url: ApiUrl.User.IMPORT }, { root: true });
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.IMPORT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.IMPORTED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.IMPORT_FAILURE, params);
        }
    },

    /**
     * パラメーターのSAML情報で接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} saml 接続先SAML情報
     * @param {any} microsoftEntraID MicrosoftEntraID接続情報
     * @param {any} oneLogin OneLogin接続情報
     * @param {any} okta Okta接続情報
     * @returns {Promise<void>}
     */
    async connection_idp({ dispatch, commit, rootState }, { saml, microsoftEntraId, oneLogin, okta }) {
        try {
            commit(types.CONNECTION_IDP_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.UserSetting.CONNECTION_IDP,
                    params: { saml: saml, microsoft_entra_id: microsoftEntraId, one_login: oneLogin, okta: okta },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CONNECTION_IDP_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.UserSetting.CONNECTED);
                dispatch('toastMessage', `${IdPType.TypeName(saml.provider)} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CONNECTION_IDP_FAILURE, params);
        }
    },

    /**
     * IdPユーザー取込処理（同期処理）
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async import_idp({ dispatch, commit, rootState }) {
        try {
            commit(types.IMPORT_IDP_START);
            const resData = await dispatch('auth/api', { method: 'post', url: ApiUrl.User.IMPORT_IDP }, { root: true });
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.IMPORT_IDP_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.User.IMPORTED_IDP);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.IMPORT_IDP_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        /* ユーザー設定はナビバーなどで使うので、画面切り替えで消してはならない */
    },
};
